import React from "react";
import { Select } from "antd";
import { BarChart2, Activity, RotateCcw, Inbox } from "react-feather";
import Tooltip from "../../../../components/Tooltip";

/** Searchable event / staff filter. Cleared or "All" values are normalised to "". */
export const FilterSelect = ({ value, onChange, options, placeholder, width = 168 }) => (
  <Select
    className="dash-select"
    popupClassName="dash-select-dropdown"
    dropdownClassName="dash-select-dropdown"
    value={value || ""}
    onChange={(v) => onChange(v || "")}
    options={options || []}
    placeholder={placeholder}
    showSearch
    optionFilterProp="label"
    style={{ width }}
    getPopupContainer={() => document.body}
  />
);

export const ChartEmpty = ({ title = "No data for this period", text = "Try a different date range or filter." }) => (
  <div className="dash-empty">
    <span className="dash-empty__icon">
      <Inbox size={18} />
    </span>
    <div className="dash-empty__title">{title}</div>
    <div className="dash-empty__text">{text}</div>
  </div>
);

export const ChartSkeleton = ({ variant = "bars" }) => (
  <div className={`dash-skeleton dash-skeleton--${variant}`} aria-hidden="true">
    {variant === "bars"
      ? Array.from({ length: 12 }).map((_, i) => (
          <span key={i} style={{ height: `${30 + ((i * 37) % 60)}%` }} />
        ))
      : <span className="dash-skeleton__ring" />}
  </div>
);

/** Bar / line switch rendered as a compact segmented control. */
export const ChartTypeToggle = ({ value, onChange }) => (
  <div className="dash-segmented dash-segmented--icon" role="group" aria-label="Chart type">
    <button
      type="button"
      className={value === "bar" ? "active" : ""}
      onClick={() => onChange("bar")}
      title="Bar chart"
      aria-pressed={value === "bar"}
    >
      <BarChart2 size={15} />
    </button>
    <button
      type="button"
      className={value === "line" ? "active" : ""}
      onClick={() => onChange("line")}
      title="Line chart"
      aria-pressed={value === "line"}
    >
      <Activity size={15} />
    </button>
  </div>
);

export const ResetButton = ({ onClick }) => (
  <button type="button" className="dash-icon-btn" onClick={onClick} title="Reset filters" aria-label="Reset filters">
    <RotateCcw size={14} />
  </button>
);

/** Legend with the period total next to each series. */
export const ChartLegend = ({ items }) => (
  <div className="dash-legend">
    {items.map((item) => (
      <div className="dash-legend__item" key={item.label}>
        <span className="dash-legend__dot" style={{ background: item.color }} />
        <span className="dash-legend__label">{item.label}</span>
        {item.value !== undefined ? <span className="dash-legend__value">{item.value}</span> : null}
      </div>
    ))}
  </div>
);

const ChartCard = ({
  title,
  subtitle,
  tooltipCode,
  actions,
  summary,
  loading = false,
  refreshing = false,
  error = false,
  empty = false,
  emptyTitle,
  emptyText,
  skeleton = "bars",
  height = 300,
  className = "",
  children,
}) => (
  <section className={`dash-card ${refreshing ? "dash-card--refreshing" : ""} ${className}`.trim()}>
    <header className="dash-card__header">
      <div className="dash-card__titles">
        <h3 className="dash-card__title">
          {title}
          {tooltipCode ? <Tooltip code={tooltipCode} /> : null}
        </h3>
        {subtitle ? <p className="dash-card__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="dash-card__actions">{actions}</div> : null}
    </header>
    {summary ? <div className="dash-card__summary">{summary}</div> : null}
    <div className="dash-card__body" style={height ? { height } : undefined}>
      {loading ? (
        <ChartSkeleton variant={skeleton} />
      ) : error ? (
        <ChartEmpty title="Couldn't load this chart" text="Check your connection and use reset to try again." />
      ) : empty ? (
        <ChartEmpty title={emptyTitle} text={emptyText} />
      ) : (
        children
      )}
    </div>
  </section>
);

export default ChartCard;
